import type { SelectProps as AriaSelectProps } from "react-aria-components";
import { Button, ListBox, Select, SelectValue } from "react-aria-components";
import React from "react";
import { tv } from "tailwind-variants";

import type { AcuityScore } from "@/core/types";
import { AcuityScoreBadge } from "@/features/acuity/components/AcuityScoreBadge";
import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { cn } from "@/lib/helpers/cn";
import { ListBoxItem2 } from "@/ui2/atoms/ListBoxItem";
import { Popover2 } from "@/ui2/atoms/Popover";

import { EscalationLabel } from "./EscalationLabel";

type SelectItem = {
  id: AcuityScore;
  name: string;
};

export type AcuityScoreSelectProps = {
  acuityScore: AcuityScore;
  value: AcuityScore;
} & Omit<AriaSelectProps<SelectItem>, "children" | "value">;

const SELECT_OPTIONS = [
  { id: 1, name: "1 – Mild" },
  { id: 2, name: "2 – Moderate" },
  { id: 3, name: "3 – Intense" },
  { id: 4, name: "4 – Severe" },
] as const satisfies readonly SelectItem[];

export function AcuityScoreSelect({ acuityScore, ...props }: AcuityScoreSelectProps) {
  const { value } = props;
  const { button, icon, listBox, popover, select } = selectStyles();

  const escalationLevel = value ? value - acuityScore : null;
  const shouldShowEscalationLabel = Boolean(escalationLevel && escalationLevel !== 0);

  return (
    <Select aria-label="Proposed Acuity Index" className={select()} name="acuity_index" {...props}>
      <Button className={cn(button(), "atom-focus group")}>
        <SelectValue aria-hidden className="tw:sr-only" />
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-1.25">
          <span className="tw:text-left tw:text-sm tw:font-bold tw:text-text-primary">
            Proposed Acuity Index:
          </span>

          {value ? (
            <>
              <AcuityScoreBadge acuityScore={value} className="tw:self-baseline" />
              {shouldShowEscalationLabel && <EscalationLabel escalationLevel={escalationLevel} />}
            </>
          ) : (
            <span className="body-sm tw:text-text-quinary">Select proposed Acuity</span>
          )}
        </div>

        <ChevronIcon className={icon()} />
      </Button>

      <Popover2 className={popover()}>
        <ListBox className={listBox()} items={SELECT_OPTIONS}>
          {(item) => (
            <ListBoxItem2 id={item.id} isDisabled={item.id === acuityScore} textValue={item.name}>
              {({ isDisabled, isSelected }) => (
                <>
                  {item.name} {isDisabled && "(current)"}
                  {isSelected && <CheckmarkIcon />}
                </>
              )}
            </ListBoxItem2>
          )}
        </ListBox>
      </Popover2>
    </Select>
  );
}

const selectStyles = tv({
  slots: {
    button: cn(
      "tw:flex tw:h-25 tw:w-full tw:auto-rows-auto tw:items-center tw:justify-between tw:gap-x-1.25 tw:px-4",
      "tw:gap-x-2 tw:text-sm",
      "tw:rounded-sm! tw:border tw:border-solid tw:border-stroke-tertiary tw:bg-fill-white",
      "tw:cursor-pointer tw:rounded",
      "tw:transition-all tw:duration-150",
      "tw:hover:border-stroke-secondary",
      "tw:data-disabled:border-stroke-tertiary tw:data-disabled:bg-fill-senary",
      "tw:disabled:cursor-not-allowed tw:disabled:text-text-quinary",
      "tw:group-data-invalid:border-stroke-error-primary",
    ),
    icon: cn(
      "tw:h-6 tw:w-6 tw:text-fill-secondary",
      "tw:transition-transform tw:duration-200",
      "tw:group-data-open:rotate-180",
    ),
    listBox: cn("tw:max-h-75 tw:overflow-y-auto", "tw:outline-none", "tw:py-1"),
    popover: "tw:w-(--trigger-width)",
    select: "tw:group tw:flex tw:flex-col tw:gap-1.5 tw:w-full",
  },
});
